import {
  DueDiligenceFunnelListStatusFilter,
  DueDiligenceFunnelSortBy,
  DueDiligenceFunnelSortDirection,
  DueDiligenceRecordStatus,
  FatSeekingInterestPageInvestmentQuery,
  StrategyDetailsPageGetDueDiligenceRecordQuery
} from '@/__generated__/graphql';
import Button from '@/components/basicComponents/button';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { INVESTMENT_QUERY } from '@/components/dealCatalog-page/seekingInterest-page/queries';
import { CREATE_DUE_DILIGENCE_RECORD_MUTATION } from '@/components/dueDiligence-page/queries';
import { useResponsive } from '@/hooks/use-responsive';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation, useQuery } from '@apollo/client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_LIST_DUE_DILIGENCE_FUNNELS_QUERY, STRATEGY_DETAILS_PAGE_START_DUE_DILIGENCE_MUTATION } from '../queries';
import StartDueDiligence from './modals/startDueDiligence';

interface ResearchTabZeroStateProps {
  investmentData: FatSeekingInterestPageInvestmentQuery['Investment'] | null;
  dueDiligenceRecordRefetch: () => Promise<ApolloQueryResult<StrategyDetailsPageGetDueDiligenceRecordQuery>>;
}

export const ResearchTabZeroState = ({ investmentData, dueDiligenceRecordRefetch }: ResearchTabZeroStateProps) => {
  const { isMobile } = useResponsive();
  const params = useParams();

  const [loading, setLoading] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [selectedDueDiligenceFunnelId, setSelectedDueDiligenceFunnelId] = useState('');
  const [listDueDiligenceFunnelsSelectOptions, setListDueDiligenceFunnelsSelectOptions] = useState<ISelectValue[]>([]);

  const [startDueDiligence] = useMutation(STRATEGY_DETAILS_PAGE_START_DUE_DILIGENCE_MUTATION, {
    awaitRefetchQueries: true,
    refetchQueries: [INVESTMENT_QUERY]
  });
  const [createDueDiligenceRecord] = useMutation(CREATE_DUE_DILIGENCE_RECORD_MUTATION);

  const { loading: listDueDiligenceFunnelsLoading } = useQuery(STRATEGY_DETAILS_PAGE_LIST_DUE_DILIGENCE_FUNNELS_QUERY, {
    variables: {
      data: {
        statusFilter: [DueDiligenceFunnelListStatusFilter.Published],
        sortDirection: DueDiligenceFunnelSortDirection.Asc,
        sortBy: DueDiligenceFunnelSortBy.Name
      }
    },
    onCompleted: (data) => {
      const selectOptions = data.listDueDiligenceFunnels.map((funnel) => createSimpleSelectOption(funnel.id, funnel.name));
      setListDueDiligenceFunnelsSelectOptions(selectOptions);
    }
  });

  const handleStartDueDiligence = async () => {
    if (investmentData?.workflowInstance?.currentState?.name === 'lead' || investmentData?.workflowInstance?.currentState?.name === 'dueDiligence') {
      try {
        setLoading(true);

        if (investmentData?.workflowInstance?.currentState?.name !== 'dueDiligence') {
          await startDueDiligence({
            variables: {
              investmentId: params?.strategyId
            }
          });
        }

        await createDueDiligenceRecord({
          variables: {
            data: {
              funnelId: selectedDueDiligenceFunnelId,
              investmentId: params?.strategyId,
              ownerEmail: investmentData?.investmentOwner?.email,
              status: DueDiligenceRecordStatus.Active
            }
          }
        });

        await dueDiligenceRecordRefetch();
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
      return;
    }
    setModalWindow({ isOpen: true, type: 'start-due-diligence' });
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'start-due-diligence' && (
        <StartDueDiligence
          isOpen={modalWindow.isOpen}
          funnelId={selectedDueDiligenceFunnelId}
          investmentData={investmentData}
          dueDiligenceRecordRefetch={dueDiligenceRecordRefetch}
          onClose={() => setModalWindow({ isOpen: false, type: '' })}
        />
      )}
      <ZeroStateWrapper>
        <ZeroStateContainer>
          <ZeroStateImageWrapper>
            <ZeroStateImage src="/static/modifyStrategy-page/descriptionZeroState.webp" draggable="false" alt="No Investors" />
          </ZeroStateImageWrapper>
          <ZeroStateTextBlock>
            <ZeroStateText>Use the Trace8 AI to manage the Due Diligence process for this strategy. Select a template to get started.</ZeroStateText>
            <Select
              value={selectedDueDiligenceFunnelId}
              setValue={setSelectedDueDiligenceFunnelId}
              options={listDueDiligenceFunnelsSelectOptions}
              size="md"
              width={isMobile ? '100%' : '50%'}
              loading={listDueDiligenceFunnelsLoading}
            />
            <Button
              onClick={handleStartDueDiligence}
              styleType={!selectedDueDiligenceFunnelId || loading ? 'disabled' : 'default'}
              size="md"
              width="max-content"
              isLoading={loading}
            >
              Start Due Diligence
            </Button>
          </ZeroStateTextBlock>
        </ZeroStateContainer>
      </ZeroStateWrapper>
    </>
  );
};

const ZeroStateWrapper = styled.div`
  padding: 40px;
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background: ${themeVar('layer.1')};
`;

const ZeroStateContainer = styled.div`
  padding: 10px 40px;
  display: flex;
  align-items: center;
  gap: 30px;
  border: 1px solid ${themeVar('border.base')};
`;

const ZeroStateImageWrapper = styled.div`
  max-width: 220px;
  max-height: 220px;
`;

const ZeroStateImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const ZeroStateTextBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const ZeroStateText = styled.p`
  font-family: Lato;
  font-size: 28px;
  font-weight: 700;
  line-height: 32px;
  color: ${themeVar('font.weak')};
`;
